import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Loader2, Pencil, Trash2, ClipboardList, Puzzle, Link2, Users, Upload, AlertTriangle, X } from "lucide-react";
import { toast } from "@/lib/toastBanner";
import ItemAssigner from "@/components/ItemAssigner";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AssessmentTypes() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scoredLexps, setScoredLexps] = useState([]);
  // Cohort assignment + CSV import state
  const [cohorts, setCohorts] = useState([]);
  const [cohortModalTarget, setCohortModalTarget] = useState(null); // assessment_type row
  const [cohortAssignments, setCohortAssignments] = useState([]); // current cohort_assessments for target
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState(null); // { assessmentId, cohortId, cohortName, assessmentName }
  const [importModalTarget, setImportModalTarget] = useState(null); // assessment_type row
  const [importFile, setImportFile] = useState(null);
  const [importCohortId, setImportCohortId] = useState("");
  const [importing, setImporting] = useState(false);
  // Auto-enroll prompt state — surfaced between preview and confirm when the
  // CSV contains emails not in the cohort roster.
  const [enrollPrompt, setEnrollPrompt] = useState(null); // { preview, errors, missing, endpointKind }
  const [form, setForm] = useState({
    name: "", input_method: "both", item_level: false, source: "native", linked_lexp_ids: [],
  });

  const fetchTypes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/assessment-types`, { withCredentials: true });
      setTypes(data);
    } catch {
      toast.error("Failed to load assessment types");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  useEffect(() => {
    axios.get(`${API}/learning-experiences/scored`, { withCredentials: true })
      .then(({ data }) => setScoredLexps(data))
      .catch(() => {});
    axios.get(`${API}/cohorts?status=active`, { withCredentials: true })
      .then(({ data }) => setCohorts(data || []))
      .catch(() => {});
  }, []);

  // ===== Cohort assignment =====
  const openAssign = async (t) => {
    setCohortModalTarget(t);
    setAssignmentLoading(true);
    try {
      const { data } = await axios.get(`${API}/assessment-types/${t.id}/cohort-assignments`, { withCredentials: true });
      setCohortAssignments(data.assigned || []);
      setCohorts(data.available_cohorts || []);
    } catch {
      toast.error("Failed to load cohort assignments");
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleAssignCohort = async (cohortId) => {
    if (!cohortModalTarget) return;
    try {
      await axios.post(
        `${API}/assessment-types/${cohortModalTarget.id}/cohort-assignments`,
        { cohort_ids: [cohortId] },
        { withCredentials: true }
      );
      toast.success("Assigned to cohort");
      openAssign(cohortModalTarget);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign");
    }
  };

  const handleRemoveCohort = async (row, purge = false) => {
    if (!cohortModalTarget) return;
    try {
      const url = `${API}/assessment-types/${cohortModalTarget.id}/cohort-assignments/${row.cohort_id}${purge ? "?purge=1" : ""}`;
      await axios.delete(url, { withCredentials: true });
      toast.success(purge ? "Removed & purged" : "Removed");
      setPurgeConfirm(null);
      openAssign(cohortModalTarget);
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail.includes("Cannot remove") && !purge) {
        setPurgeConfirm({
          cohortId: row.cohort_id,
          cohortName: row.cohort_name,
          scoreCount: row.score_count,
          itemCount: row.item_response_count,
          row,
        });
      } else {
        toast.error(detail || "Failed to remove");
      }
    }
  };

  // ===== CSV Import =====
  const openImport = (t) => {
    setImportModalTarget(t);
    setImportFile(null);
    setImportCohortId("");
    axios.get(`${API}/assessment-types/${t.id}/cohort-assignments`, { withCredentials: true })
      .then(({ data }) => {
        // Pre-populate cohort picker with already-assigned cohorts (CSV import assumes cohort assigned)
        const opts = (data.assigned || [])
          .filter((x) => x.cohort_id)
          .map((x) => ({ id: x.cohort_id, name: x.cohort_name }));
        setCohorts(opts);
      })
      .catch(() => {});
  };

  const handleImportCsv = async () => {
    if (!importFile) { toast.error("Pick a CSV file first"); return; }
    if (!importCohortId) { toast.error("Select a cohort"); return; }
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", importFile);
      const kind = importModalTarget.assessment_kind === "external" ? "items" : "scores";
      const endpoint = `${API}/cohorts/${importCohortId}/assessments/${importModalTarget.id}/${kind}/import`;
      const { data: preview } = await axios.post(endpoint, form, { withCredentials: true });

      const missing = (preview.errors || []).filter((e) => e.reason === "not_in_roster");
      if (missing.length > 0) {
        // Pause and ask the admin whether to auto-enroll the missing learners
        // before finalizing the import.
        setEnrollPrompt({ preview, errors: preview.errors || [], missing, endpointKind: kind });
        setImporting(false);
        return;
      }

      await confirmHistoricalImport(preview.preview || [], preview.errors || [], kind);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
      setImporting(false);
    }
  };

  const confirmHistoricalImport = async (rows, errors, kind) => {
    setImporting(true);
    try {
      const confirmUrl = `${API}/cohorts/${importCohortId}/assessments/${importModalTarget.id}/${kind}/confirm`;
      const confirmBody = kind === "items"
        ? { items: rows }
        : { scores: rows, errors };
      const { data: confirmRes } = await axios.post(confirmUrl, confirmBody, { withCredentials: true });
      const okCount = confirmRes.imported || 0;
      const errCount = errors.length;
      toast.success(`Imported ${okCount} row(s)${errCount ? ` · ${errCount} skipped` : ""}`);
      setImportModalTarget(null);
      setImportFile(null);
      setEnrollPrompt(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const runAutoEnrollAndImport = async () => {
    if (!enrollPrompt) return;
    const { preview, errors, missing, endpointKind } = enrollPrompt;
    setImporting(true);
    try {
      // 1. Bulk-enroll the missing learners in this cohort
      const { data: enrollRes } = await axios.post(
        `${API}/cohorts/${importCohortId}/learners/bulk-enroll`,
        {
          learners: missing.map((m) => ({
            first_name: m.first_name || "",
            last_name: m.last_name || "",
            email: m.email,
          })),
        },
        { withCredentials: true },
      );
      const emailToId = enrollRes.email_to_learner_id || {};
      // 2. Fold the newly-enrolled rows into the confirm payload
      const extraRows = missing
        .filter((m) => emailToId[m.email])
        .map((m) => (endpointKind === "items"
          ? {
              email: m.email, learner_id: emailToId[m.email],
              item_id: m.item_id, item_name: m.item_name,
              correct: m.correct,
            }
          : {
              email: m.email, learner_id: emailToId[m.email],
              first_name: m.first_name, last_name: m.last_name,
              score: m.score,
            }));
      const combinedRows = [...(preview.preview || []), ...extraRows];
      // 3. Errors that remain after we successfully enrolled — anything with a
      //    different `reason` or that failed to enroll
      const enrolledEmails = new Set(Object.keys(emailToId));
      const remainingErrors = errors.filter((e) => !(e.reason === "not_in_roster" && enrolledEmails.has(e.email)));
      // 4. Run confirm with the enriched payload
      await confirmHistoricalImport(combinedRows, remainingErrors, endpointKind);
      if (enrollRes.added) {
        toast.success(`Enrolled ${enrollRes.added} new learner(s) in cohort`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Auto-enroll failed");
      setImporting(false);
    }
  };

  const declineAutoEnrollAndImport = async () => {
    if (!enrollPrompt) return;
    const { preview, errors, endpointKind } = enrollPrompt;
    await confirmHistoricalImport(preview.preview || [], errors, endpointKind);
  };

  const openAdd = () => {
    setEditTarget(null);
    setForm({ name: "", input_method: "both", item_level: true, source: "native", linked_lexp_ids: [], assessment_kind: "native" });
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditTarget(t);
    setForm({
      name: t.name, input_method: t.input_method || "both", item_level: true,
      source: t.source || "native", linked_lexp_ids: t.linked_lexp_ids || [],
      assessment_kind: t.assessment_kind || "native",
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editTarget) {
        await axios.put(`${API}/assessment-types/${editTarget.id}`, form, { withCredentials: true });
        toast.success("Assessment type updated");
      } else {
        await axios.post(`${API}/assessment-types`, form, { withCredentials: true });
        toast.success("Assessment type created");
      }
      setShowModal(false);
      fetchTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const t = deleteTarget;
    if (!t) return;
    try {
      await axios.delete(`${API}/assessment-types/${t.id}`, { withCredentials: true });
      toast.success("Assessment type deleted");
      fetchTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="assessment-types-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight" data-testid="assessment-types-title">
            Historical Assessments
          </h2>
          <p className="text-[#6E7787] font-body text-sm mt-1">Containers for historical scored data — create once, then capture per-question records via Cohort Assessments.</p>
        </div>
        <Button
          onClick={openAdd}
          data-testid="create-assessment-type-btn"
          className="h-10 px-5 font-body font-medium text-white rounded-lg hover:opacity-90 shrink-0"
          style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
        >
          <Plus className="h-4 w-4 mr-1.5" />
          Create Assessment
        </Button>
      </div>

      {/* Phase 9: Historical-only framing */}
      <div
        className="rounded-lg border px-4 py-3 flex items-start gap-3"
        style={{
          backgroundColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 10%, transparent)",
          borderColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 30%, transparent)",
        }}
        data-testid="historical-assessments-banner"
      >
        <ClipboardList className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "var(--primary-color, #4A90D9)" }} />
        <div className="text-xs font-body leading-relaxed" style={{ color: "var(--primary-color, #4A90D9)" }}>
          <strong>Historical-only.</strong> Assessments capture past performance — they don't fire Learning Logic and aren't counted in Track totals. For learner-facing data capture use <em>Learning Experiences</em>. For end-of-track feedback use <em>Surveys</em>.
        </div>
      </div>

      {types.length === 0 ? (
        <div className="bg-[#11141A] border border-dashed border-white/10 rounded-xl p-12 text-center" data-testid="assessment-types-empty">
          <ClipboardList className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--primary-color, #4A90D9)" }} />
          <h3 className="font-body text-lg font-bold text-white mb-2">No assessments yet</h3>
          <p className="text-[#6E7787] font-body text-sm mb-4">Create your first assessment to begin building your program</p>
          <Button
            onClick={openAdd}
            className="h-9 px-4 font-body text-white rounded-lg hover:opacity-90"
            style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Create Assessment
          </Button>
        </div>
      ) : (
        <div className="bg-[#11141A] border border-white/10 rounded-xl overflow-hidden" data-testid="assessment-types-table">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px]">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-5 py-3.5 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Name</th>
                  <th className="text-left px-5 py-3.5 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Linked LEXPs</th>
                  <th className="text-right px-5 py-3.5 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {types.map((t) => (
                  <tr key={t.id} className={`border-b border-white/5 hover:bg-white/[0.02] transition-colors cursor-pointer ${selectedType?.id === t.id ? "bg-white/[0.04]" : ""}`} data-testid={`at-row-${t.id}`} onClick={() => setSelectedType(selectedType?.id === t.id ? null : t)}>
                    <td className="px-5 py-3.5 text-sm font-body font-medium text-white" data-testid={`at-name-${t.id}`}>
                      <div className="flex items-center gap-2">
                        {t.name}
                        {t.assessment_kind === "external" && (
                          <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/20 text-[10px]">External</Badge>
                        )}
                        {selectedType?.id === t.id && <Puzzle className="h-3.5 w-3.5" style={{ color: "var(--primary-color)" }} />}
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      {(t.linked_lexp_ids || []).length > 0 ? (
                        <div className="flex items-center gap-1.5">
                          <Link2 className="h-3.5 w-3.5 text-cyan-400" />
                          <span className="text-sm font-body text-cyan-400">{t.linked_lexp_ids.length}</span>
                        </div>
                      ) : (
                        <span className="text-sm font-body text-[#6E7787]">&mdash;</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openAssign(t); }} data-testid={`assign-cohort-${t.id}`} className="h-7 w-7 p-0 text-[#6E7787] hover:text-white hover:bg-white/5" title="Manage Cohorts">
                          <Users className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openImport(t); }} data-testid={`import-csv-${t.id}`} className="h-7 w-7 p-0 text-[#6E7787] hover:text-white hover:bg-white/5" title="Import CSV">
                          <Upload className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(t); }} data-testid={`edit-at-${t.id}`} className="h-7 w-7 p-0 text-[#6E7787] hover:text-white hover:bg-white/5" title="Edit">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setDeleteTarget(t); }} data-testid={`delete-at-${t.id}`} className="h-7 w-7 p-0 text-[#6E7787] hover:text-red-400 hover:bg-red-500/10" title="Delete">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                        </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Item Bank for selected type */}
      {selectedType && (
        <div className="bg-[#11141A] border border-white/10 rounded-xl p-6" data-testid="assessment-item-bank">
          <p className="text-xs text-[#6E7787] font-body mb-4">Item Bank for: <span className="text-white font-medium">{selectedType.name}</span></p>
          <ItemAssigner entityType="assessment" entityId={selectedType.id} />
        </div>
      )}

      {/* Create / Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-md" data-testid="at-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-xl text-white">
              {editTarget ? "Edit Assessment" : "Create Assessment"}
            </DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body">
              {editTarget ? "Update assessment details." : "Define a new assessment for your program."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">Name <span className="text-red-400">*</span></Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="at-name-input"
                placeholder="e.g. HESI Exam, Clinical Competency"
                className="bg-[#0A0C10] border-white/10 text-white focus:border-[var(--primary-color,#4A90D9)] h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">Assessment Type</Label>
              <div className="flex gap-2">
                {[{ v: "native", l: "Native" }, { v: "external", l: "External" }].map((k) => (
                  <button key={k.v} type="button"
                    onClick={() => !editTarget && setForm({ ...form, assessment_kind: k.v })}
                    disabled={!!editTarget}
                    data-testid={`at-kind-${k.v}`}
                    className={`px-4 py-2 text-sm font-body rounded-lg border transition-colors ${form.assessment_kind === k.v ? "border-white/20 bg-white/10 text-white" : "border-white/10 text-[#6E7787] hover:text-white hover:border-white/15"} ${editTarget ? "opacity-50 cursor-not-allowed" : ""}`}
                  >{k.l}</button>
                ))}
              </div>
              <p className="text-[10px] text-[#6E7787] font-body">
                {form.assessment_kind === "external"
                  ? "External assessments contain External Items only. Score computed from correct/incorrect."
                  : "Native assessments contain standard question types (MC, SATA, Task, Likert)."}
              </p>
            </div>
            {/* Link Scored LEXPs */}
            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm flex items-center gap-1.5">
                <Link2 className="h-3.5 w-3.5" /> Link Scored LEXPs
              </Label>
              <p className="text-[10px] text-[#6E7787] font-body -mt-0.5">Linked LEXP scores roll up into this assessment's gradebook entry</p>
              {scoredLexps.length === 0 ? (
                <p className="text-xs text-[#6E7787] font-body italic py-1">No scored LEXPs available</p>
              ) : (
                <div className="bg-[#0A0C10] border border-white/10 rounded-md max-h-36 overflow-y-auto" data-testid="linked-lexp-selector">
                  {scoredLexps.map((le) => {
                    const isLinked = (form.linked_lexp_ids || []).includes(le.id);
                    return (
                      <button
                        key={le.id}
                        type="button"
                        data-testid={`link-lexp-${le.id}`}
                        onClick={() => {
                          const ids = form.linked_lexp_ids || [];
                          setForm({
                            ...form,
                            linked_lexp_ids: isLinked ? ids.filter((x) => x !== le.id) : [...ids, le.id],
                          });
                        }}
                        className={`w-full text-left px-3 py-2 text-sm font-body border-b border-white/5 last:border-0 transition-colors ${
                          isLinked ? "bg-cyan-500/10 text-cyan-300" : "text-[#A1A9B8] hover:bg-white/[0.03]"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className={`h-3.5 w-3.5 rounded border flex items-center justify-center text-[10px] ${
                            isLinked ? "bg-cyan-500 border-cyan-500 text-white" : "border-white/20"
                          }`}>
                            {isLinked && "✓"}
                          </span>
                          {le.title}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setShowModal(false)} className="text-[#A1A9B8] hover:text-white hover:bg-white/5" data-testid="at-modal-cancel">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                data-testid="at-modal-save"
                className="h-10 px-5 font-body font-medium text-white rounded-lg hover:opacity-90"
                style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                {editTarget ? "Save Changes" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cohort Assignment Modal */}
      <Dialog open={!!cohortModalTarget} onOpenChange={(open) => { if (!open) { setCohortModalTarget(null); setPurgeConfirm(null); } }}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-xl max-h-[85vh] p-0 flex flex-col" data-testid="at-cohort-modal">
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b border-white/5">
            <DialogTitle className="font-body font-bold text-lg text-white flex items-center gap-2">
              <Users className="h-4 w-4" style={{ color: "var(--primary-color, #4A90D9)" }} />
              Manage Cohorts — {cohortModalTarget?.name}
            </DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body text-xs">
              Assign or remove cohorts. Removing a cohort with score data will prompt for purge confirmation.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
          {assignmentLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
            </div>
          ) : (
            <div className="space-y-5">
              {/* Assigned cohorts */}
              <div>
                <Label className="text-[#A1A9B8] font-body text-xs uppercase tracking-wider mb-2 block">Assigned Cohorts ({cohortAssignments.length})</Label>
                {cohortAssignments.length === 0 ? (
                  <p className="text-xs text-[#6E7787] font-body italic py-2">Not assigned to any cohort yet.</p>
                ) : (
                  <div className="bg-[#0A0C10] border border-white/10 rounded-md divide-y divide-white/5">
                    {cohortAssignments.map((row) => (
                      <div key={row.assessment_id} className="flex items-center justify-between px-3 py-2.5" data-testid={`assigned-cohort-${row.cohort_id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-body text-white truncate">{row.cohort_name}</span>
                          {row.has_data ? (
                            <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/20 text-[10px] shrink-0">
                              {row.score_count > 0 ? `${row.score_count} score${row.score_count === 1 ? "" : "s"}` : ""}
                              {row.score_count > 0 && row.item_response_count > 0 ? " · " : ""}
                              {row.item_response_count > 0 ? `${row.item_response_count} item${row.item_response_count === 1 ? "" : "s"}` : ""}
                            </Badge>
                          ) : (
                            <Badge className="bg-white/5 text-[#6E7787] border-white/10 text-[10px] shrink-0">No data</Badge>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveCohort(row, false)}
                          data-testid={`remove-cohort-${row.cohort_id}`}
                          className="h-7 px-2 text-xs text-[#6E7787] hover:text-red-400 hover:bg-red-500/10"
                        >
                          <X className="h-3.5 w-3.5 mr-1" /> Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Available cohorts to assign */}
              <div>
                <Label className="text-[#A1A9B8] font-body text-xs uppercase tracking-wider mb-2 block">Available Cohorts ({cohorts.length})</Label>
                {cohorts.length === 0 ? (
                  <p className="text-xs text-[#6E7787] font-body italic py-2">All active cohorts are already assigned.</p>
                ) : (
                  <div className="bg-[#0A0C10] border border-white/10 rounded-md max-h-48 overflow-y-auto divide-y divide-white/5">
                    {cohorts.map((c) => (
                      <div key={c.id} className="flex items-center justify-between px-3 py-2.5" data-testid={`available-cohort-${c.id}`}>
                        <span className="text-sm font-body text-white">{c.name}</span>
                        <Button
                          size="sm"
                          onClick={() => handleAssignCohort(c.id)}
                          data-testid={`assign-to-${c.id}`}
                          className="h-7 px-3 text-xs font-body text-white hover:opacity-90"
                          style={{ backgroundColor: "var(--primary-color, #4A90D9)" }}
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" /> Assign
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Purge confirm inline banner */}
              {purgeConfirm && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 space-y-3" data-testid="purge-confirm-banner">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
                    <div className="text-xs font-body text-red-200 leading-relaxed">
                      <strong>{purgeConfirm.cohortName}</strong> has {purgeConfirm.scoreCount} score(s) and {purgeConfirm.itemCount} item response(s). Removing will permanently delete all historical data for this cohort × assessment pair.
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => setPurgeConfirm(null)} className="h-8 text-xs text-[#A1A9B8] hover:text-white hover:bg-white/5" data-testid="purge-cancel">
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleRemoveCohort(purgeConfirm.row, true)}
                      data-testid="purge-confirm-btn"
                      className="h-8 px-3 text-xs bg-red-500 hover:bg-red-600 text-white"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete All Data & Remove
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          </div>

          <div className="flex justify-end px-6 py-3 border-t border-white/5 shrink-0 bg-[#11141A]">
            <Button variant="ghost" onClick={() => { setCohortModalTarget(null); setPurgeConfirm(null); }} className="text-[#A1A9B8] hover:text-white hover:bg-white/5" data-testid="at-cohort-modal-close">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CSV Import Modal */}
      <Dialog open={!!importModalTarget} onOpenChange={(open) => { if (!open) { setImportModalTarget(null); setImportFile(null); } }}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-lg" data-testid="at-import-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-xl text-white flex items-center gap-2">
              <Upload className="h-5 w-5" style={{ color: "var(--primary-color, #4A90D9)" }} />
              Import Historical Data — {importModalTarget?.name}
            </DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body">
              Upload a CSV mapped by learner email. Records are imported as historical data (response_origin = imported).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">Target Cohort <span className="text-red-400">*</span></Label>
              {cohorts.length === 0 ? (
                <p className="text-xs text-amber-400 font-body py-1">
                  This assessment isn't assigned to any cohort yet. Assign it first, then return here to import.
                </p>
              ) : (
                <select
                  value={importCohortId}
                  onChange={(e) => setImportCohortId(e.target.value)}
                  data-testid="import-cohort-select"
                  className="w-full bg-[#0A0C10] border border-white/10 text-white rounded-md h-10 px-3 text-sm font-body focus:border-[var(--primary-color,#4A90D9)] focus:outline-none"
                >
                  <option value="">-- Select cohort --</option>
                  {cohorts.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              )}
              <p className="text-[10px] text-[#6E7787] font-body">Only cohorts this assessment is already assigned to are shown.</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">CSV File <span className="text-red-400">*</span></Label>
              <Input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                data-testid="import-csv-input"
                className="bg-[#0A0C10] border-white/10 text-white h-10 file:mr-3 file:rounded file:border-0 file:bg-white/5 file:px-3 file:py-1 file:text-xs file:text-white hover:file:bg-white/10"
              />
              <div className="rounded-md bg-[#0A0C10] border border-white/10 p-3 text-[11px] font-body text-[#A1A9B8] leading-relaxed">
                {importModalTarget?.assessment_kind === "external" ? (
                  <>
                    <strong className="text-white">Expected columns</strong> (external): <code className="text-cyan-300">email, item_id, item_name, correct</code>
                    <br />Example: <code className="text-cyan-300">jane@acme.com,q42,Pharmacology Q42,true</code>
                  </>
                ) : (
                  <>
                    <strong className="text-white">Expected columns</strong> (native): <code className="text-cyan-300">email, score</code> (0–100)
                    <br />Example: <code className="text-cyan-300">jane@acme.com,87.5</code>
                  </>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => { setImportModalTarget(null); setImportFile(null); }} className="text-[#A1A9B8] hover:text-white hover:bg-white/5" data-testid="at-import-cancel">
                Cancel
              </Button>
              <Button
                onClick={handleImportCsv}
                disabled={importing || !importFile || !importCohortId}
                data-testid="at-import-submit"
                className="h-10 px-5 font-body font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
              >
                {importing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
                Import
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        tier={2}
        entityName={deleteTarget?.name}
        entityType="assessment"
        entityId={deleteTarget?.id}
        onConfirm={handleDelete}
      />

      {/* Auto-enroll prompt — shown after preview when the CSV includes rows
          whose emails aren't in the cohort roster yet. */}
      <Dialog open={!!enrollPrompt} onOpenChange={(v) => { if (!v && !importing) setEnrollPrompt(null); }}>
        <DialogContent className="max-w-lg bg-[#0F1216] border-white/10 text-white" data-testid="enroll-prompt-modal">
          <DialogHeader>
            <DialogTitle className="text-white font-heading inline-flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              Missing learners
            </DialogTitle>
            <DialogDescription className="text-[#A1A9B8] font-body">
              This import includes <strong className="text-white">{enrollPrompt?.missing?.length || 0}</strong> learner{(enrollPrompt?.missing?.length || 0) === 1 ? "" : "s"} not currently enrolled in this cohort. Would you like to add them and import their scores?
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-56 overflow-y-auto border border-white/10 rounded-md bg-[#0A0C10]" data-testid="enroll-prompt-list">
            <ul className="divide-y divide-white/5 text-xs font-body">
              {(enrollPrompt?.missing || []).map((m, i) => (
                <li key={`${m.email}-${i}`} className="px-3 py-1.5">
                  <span className="text-white">{((m.first_name || "") + " " + (m.last_name || "")).trim() || "(no name)"}</span>
                  <span className="text-[#6E7787]"> · {m.email}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="outline"
              onClick={declineAutoEnrollAndImport}
              disabled={importing}
              className="border-white/15 text-white bg-transparent hover:bg-white/5"
              data-testid="enroll-prompt-decline"
            >
              Skip &amp; import matched only
            </Button>
            <Button
              onClick={runAutoEnrollAndImport}
              disabled={importing}
              style={{ background: "var(--primary-color)", color: "var(--admin-button-fg, #fff)" }}
              data-testid="enroll-prompt-confirm"
            >
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enroll & import all"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
